import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import { Navbar } from "@/components/Navbar";
import { RiskRemediation } from "@/components/RiskRemediation";
import { IntroNote } from "@/components/IntroNote";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Zap, Check, X } from "lucide-react";

const STATE_STYLES = {
  proposed: "bg-amber-100 text-amber-700",
  approved: "bg-blue-100 text-blue-700",
  executing: "bg-blue-100 text-blue-700",
  executed: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

const StatePill = ({ state }) => (
  <span
    data-testid={`remediation-state-${state}`}
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${
      STATE_STYLES[state] || STATE_STYLES.proposed
    }`}
  >
    {state}
  </span>
);

const ActionApprovals = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  const load = useCallback(() => {
    return api
      .get("/remediation-actions")
      .then(({ data }) => setActions(data))
      .catch(() => toast.error("Failed to load remediation queue"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openApprove = (action) => { setApproving(action); setReason(""); setReasonError(""); };

  const confirmApprove = async () => {
    if (reason.trim().length < 20) { setReasonError("Reason must be at least 20 characters."); return; }
    try {
      await api.post(`/remediation-actions/${approving.action_id}/approve`, { reason });
      toast.success("Remediation approved and executed");
      setApproving(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    }
  };

  const reject = async (action) => {
    try {
      await api.post(`/remediation-actions/${action.action_id}/reject`, { reason: "Rejected by approver" });
      toast.success("Remediation rejected");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    }
  };

  return (
    <div>
      <p className="max-w-2xl text-sm text-ek-ink/65">
        Automated actions require explicit human approval before execution. Every approval and execution is
        written to the audit log.
      </p>
      <div className="mt-6 space-y-3">
        {loading ? (
          <p className="py-12 text-center text-sm text-ek-ink/65">Loading queue…</p>
        ) : actions.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-md border border-black/5 bg-white py-16 text-ek-ink/65">
            <ShieldCheck size={32} className="text-ek-purple/40" />
            <p className="text-sm">No remediation actions yet.</p>
            <p className="text-xs">Request one from an auto-remediation playbook step.</p>
          </div>
        ) : (
          actions.map((a) => (
            <div key={a.action_id} data-testid="remediation-row"
              className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-black/5 bg-white p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ek-purple/10 text-ek-purple">
                  <Zap size={18} />
                </div>
                <div>
                  <p className="font-medium text-ek-ink">{a.simulated_effect}</p>
                  <p className="text-xs text-ek-ink/65">
                    Step {a.step_no} · tier {a.tier} · run {a.run_id.slice(0, 10)}…
                  </p>
                  {a.reason && <p className="mt-1 text-xs italic text-ek-ink/65">“{a.reason}”</p>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatePill state={a.state} />
                {a.state === "proposed" && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => openApprove(a)} data-testid="approve-remediation-button"
                      className="flex items-center gap-1 rounded-full bg-ek-purple px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-ek-lilac">
                      <Check size={13} /> Approve
                    </button>
                    <button onClick={() => reject(a)} data-testid="reject-remediation-button"
                      className="flex items-center gap-1 rounded-full border border-black/10 px-4 py-1.5 text-xs font-semibold text-ek-ink/60 transition-colors hover:border-red-300 hover:text-red-600">
                      <X size={13} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Approve remediation</DialogTitle>
            <DialogDescription>
              {approving?.simulated_effect}. Provide a justification (min 20 characters) — it is written to the audit log.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={reason}
            onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(""); }}
            rows={3} data-testid="approve-reason-input"
            placeholder="e.g. Verified blast radius and rollback plan with SOC lead…"
            className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-ek-ink outline-none transition-colors focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
          />
          <div className="flex items-center justify-between">
            <span className={`text-xs ${reason.length < 20 ? "text-ek-ink/65" : "text-emerald-600"}`}>{reason.length}/20</span>
            {reasonError && <span className="text-xs text-red-600" data-testid="approve-reason-error">{reasonError}</span>}
          </div>
          <DialogFooter>
            <button onClick={confirmApprove} data-testid="approve-confirm-button" className="ek-btn w-full">
              Approve &amp; execute
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function Remediation() {
  const { user } = useAuth();
  const canSeeRisk = hasPermission(user, "risk.read");
  const canApprove = hasPermission(user, "remediation.read");
  const [tab, setTab] = useState(canSeeRisk ? "plans" : "approvals");

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-12" data-testid="remediation-page">
        <p className="ek-eyebrow">Remediation &amp; Accountability</p>
        <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Remediation</h1>
        <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
          Drive risks down with accountable owners, due dates and a live burn-down — and approve
          automated playbook actions before they execute.
        </p>

        <IntroNote icon={ShieldCheck} title="What you're looking at" testid="remediation-intro" className="mt-6">
          Where risk gets driven down and held accountable. "Risk remediation" tracks plans with named owners,
          due dates and a live burn-down toward a target residual score; "Action approvals" is the human gate
          where automated playbook actions must be approved before they execute — every step audited. Completing
          a plan can auto-raise a record in your connected Jira/ServiceNow.
        </IntroNote>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList data-testid="remediation-tabs">
            {canSeeRisk && <TabsTrigger value="plans" data-testid="tab-plans">Risk remediation</TabsTrigger>}
            {canApprove && <TabsTrigger value="approvals" data-testid="tab-approvals">Action approvals</TabsTrigger>}
          </TabsList>
          {canSeeRisk && (
            <TabsContent value="plans" className="mt-6"><RiskRemediation /></TabsContent>
          )}
          {canApprove && (
            <TabsContent value="approvals" className="mt-6"><ActionApprovals /></TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
